import { useEffect, useState } from 'react';

import http from '../services/http';

/**
 * O DEPARTAMENTO do chamado, para aparecer onde o responsável aparece.
 *
 * ## O pedido (pontos 21 e 24 da Gisele, 20/10/2026)
 *
 * "Mostrar o departamento selecionado junto com o responsável" e "com só o
 * departamento selecionado, o ticket aparece no Kanban como 'Não atribuído',
 * identificando o departamento".
 *
 * São a mesma falta vista de dois lugares. Desde 19/10 escolher um
 * departamento como responsável zera o `assigned_to` de propósito -- ninguém
 * assumiu -- e grava `tickets.service_group_id`. O chamado então aparece como
 * "Não atribuído" em todo lugar, e **qual** departamento está esperando por
 * ele some da tela: o seletor separado foi removido na mesma data, porque dois
 * campos para a mesma decisão confundiam.
 *
 * ## Por que o mapa vem do navegador, e não do payload
 *
 * Mesma decisão do representante (`utils/nomeDoRepresentante`): a listagem já
 * foi de 19 MB para 6,7 MB cortando coluna, e anexar a descrição do
 * departamento em cada chamado a devolveria por linha. O mapa
 * `id -> descrição` é uma requisição só, por sessão, e resolve no cliente.
 */

/** Promessa compartilhada: uma requisição por sessão, não por componente. */
let promessaDosDepartamentos = null;

export const useDepartamentos = () => {
    const [porId, setPorId] = useState({});

    useEffect(() => {
        let cancelado = false;

        if (!promessaDosDepartamentos) {
            promessaDosDepartamentos = http.get('/groups')
                .then(({ data }) => {
                    const mapa = {};
                    (Array.isArray(data) ? data : []).forEach((g) => {
                        if (g?.id != null) mapa[String(g.id)] = g.description || g.code || '';
                    });
                    return mapa;
                })
                // A falha NÃO fica em cache: um erro de rede momentâneo não
                // pode deixar o rótulo ausente pelo resto da sessão. E falhar
                // devolve o rótulo de sempre -- nunca a tela quebrada.
                .catch(() => { promessaDosDepartamentos = null; return {}; });
        }

        promessaDosDepartamentos.then((mapa) => {
            if (!cancelado) setPorId(mapa);
        });

        return () => { cancelado = true; };
    }, []);

    return porId;
};

/**
 * O nome do departamento do chamado, ou vazio.
 *
 * Vazio quando o chamado não tem departamento, e também quando o mapa ainda
 * não chegou ou falhou: nos três casos o rótulo volta a ser o de antes, que é
 * o degrau certo para cair.
 *
 * Departamento DESATIVADO continua resolvendo o nome: o valor é histórico, e
 * esconder a descrição deixaria um chamado real parecendo sem departamento.
 */
export const nomeDoDepartamento = (chamado, departamentos) => {
    const id = chamado?.service_group_id;

    if (id == null || id === '') return '';

    return (departamentos || {})[String(id)] || '';
};
